/**
 * Whether a group was measured with the engines and the configuration deployed now.
 *
 * The groups list has no field that says so. What it has is a proxy: the engine versions of the
 * latest run that registered engines (`queue.versions.worker`), set against the versions of the
 * group's own latest successful run, which a one-row probe reads (`ENGINE_PROBE_PARAMS`). Until
 * that probe answers, a group is «not checked» and never «current»: calling it current on anything
 * less would hide exactly the group somebody needs to relaunch. A current Virtual Profiler answers
 * this itself (`GroupReanalysis`, read by `freshnessFromReanalysis`), and then no probe is sent.
 *
 * A drift is not always a new series. Upstream's series key carries the major and minor version of
 * gestures and coherence (`SERIES_ENGINES`), so a patch bump keeps the figures comparable and a
 * minor one starts a series whose figures are not compared with the old ones. The screen says
 * which, because that is what decides whether relaunching is a check or a reset.
 *
 * `worker.degraded` plays no part here. It says some engine fell back silently, which is about how
 * the last run went, not about which versions are deployed; the Motores card shows it on its own.
 */
import type { ListParams } from '@beonit/adaptive-raindrop';
import type {
  AnalysisConfigSummary,
  AnalysisGroup,
  EngineVersions,
  GroupReanalysis,
  RunFilters,
} from '../model.js';
import type { EngineChange } from './config-diff.js';
import { engineShortName } from './engine-names.js';
import { applySelection, DEFAULT_ENGINE_CATALOGUE, engineLabel } from './engine-rules.js';
import { groupInFlight } from './hub-digest.js';
import { VIDEO_SOURCE } from './launch-options.js';

/** The engines whose major.minor version is part of upstream's series key. */
export const SERIES_ENGINES: readonly string[] = Object.freeze(['gestures', 'coherence']);

/** How many groups a sweep asks about at a time. */
export const PROBE_WIDTH = 4;

/** One row is enough: the probe only wants the latest successful run of a group. */
export const ENGINE_PROBE_PARAMS: Readonly<ListParams> = Object.freeze({ page: 1, pageSize: 1 });

/** «a, b y c»: how a sentence lists things. One for the groups board's libs. */
export const AND_LIST = new Intl.ListFormat('es', { style: 'long', type: 'conjunction' });

/**
 * The filters of a group's engine probe: its successful runs, newest first.
 *
 * Successful only, because a failed run may have died before recording its engines, and because
 * the figures on the card come from successful runs: those are the versions they were measured
 * with.
 */
export function engineProbeFilters(groupId: number): RunFilters {
  return { groupId: String(groupId), status: ['SUCCEEDED'] };
}

const MAJOR_MINOR = /^v?(\d+)(?:\.(\d+))?/;

/**
 * «3.1.0» → «3.1», the part of a version the series key carries; null when unreadable.
 *
 * The same reading as upstream's `seriesKey.ts`, leading zeros and a missing minor included: two
 * spellings that upstream folds into one series must not be called a new series here.
 */
export function majorMinor(version: string | null | undefined): string | null {
  const match = MAJOR_MINOR.exec((version ?? '').trim());
  if (!match) return null;

  return `${Number(match[1])}.${Number(match[2] ?? 0)}`;
}

/** The engines a group actually runs, dependencies included (coherence pulls in gestures). */
export function engineScope(engines: readonly string[]): string[] {
  return applySelection(DEFAULT_ENGINE_CATALOGUE, VIDEO_SOURCE, engines).engines;
}

export interface EngineDrift {
  engine: string;
  /** Null when the group's run did not record that engine. */
  before: string | null;
  after: string;
  breaksSeries: boolean;
}

/**
 * - `never_run`: nothing to compare.
 * - `running`: the next result will answer it.
 * - `unknown`: no worker versions, not probed yet, no engine data, or the probe failed.
 * - `current` / `outdated`: the probe answered.
 */
export type EngineFreshness =
  | { kind: 'never_run' }
  | { kind: 'running' }
  | { kind: 'unknown'; reason: 'no_worker' | 'not_checked' | 'no_engine_data' | 'check_failed' }
  | { kind: 'current' }
  | { kind: 'outdated'; drift: EngineDrift[]; breaksSeries: boolean };

/** Gestures and coherence first, the rest alphabetically: the order every engine list reads in. */
function engineOrder(a: string, b: string): number {
  const rank = (name: string): number => {
    const index = SERIES_ENGINES.indexOf(name);

    return index === -1 ? SERIES_ENGINES.length : index;
  };

  return rank(a) - rank(b) || a.localeCompare(b);
}

/** Whether moving an engine from one version to another starts a new series upstream. */
function breaksSeriesOf(engine: string, before: string | null, after: string | null): boolean {
  return SERIES_ENGINES.includes(engine) && majorMinor(before) !== majorMinor(after);
}

function hasNeverRun(group: Pick<AnalysisGroup, 'runs' | 'lastBatch'>): boolean {
  return group.runs === 0 && group.lastBatch === null;
}

export function engineFreshness(input: {
  group: Pick<AnalysisGroup, 'latestParameters' | 'runs' | 'lastBatch' | 'runsByStatus'>;
  worker: Pick<EngineVersions, 'engines'> | null | undefined;
  /** `undefined` = not probed yet; `null` = the group has no successful run with engines. */
  measured: Record<string, string> | null | undefined;
  checkFailed?: boolean;
  /**
   * In flight by the queue, which can see a relaunch before the list does. Such a group is not
   * outdated: its next result answers the question, and counting it in «Reanalizar N» would
   * relaunch what is already running.
   */
  inFlight?: boolean;
}): EngineFreshness {
  const { group, worker, measured } = input;

  if (hasNeverRun(group)) return { kind: 'never_run' };
  if (groupInFlight(group) > 0 || input.inFlight === true) return { kind: 'running' };

  const deployed = worker?.engines ?? {};
  if (Object.keys(deployed).length === 0) return { kind: 'unknown', reason: 'no_worker' };
  if (input.checkFailed) return { kind: 'unknown', reason: 'check_failed' };
  if (measured === undefined) return { kind: 'unknown', reason: 'not_checked' };
  if (measured === null) return { kind: 'unknown', reason: 'no_engine_data' };

  // The engines its latest execution was launched with; one that recorded none (older than the
  // launch records) is scoped by what its run recorded, the best statement left.
  const scope = engineScope(group.latestParameters?.engines ?? []);
  const candidates = scope.length > 0 ? scope : Object.keys(measured);
  const keys = candidates
    .filter((engine) => engine in deployed && engine in measured)
    .sort(engineOrder);

  if (keys.length === 0) return { kind: 'unknown', reason: 'no_engine_data' };

  const drift: EngineDrift[] = keys
    .filter((engine) => measured[engine] !== deployed[engine])
    .map((engine) => {
      const before = measured[engine] ?? null;
      const after = deployed[engine] as string;

      return { engine, before, after, breaksSeries: breaksSeriesOf(engine, before, after) };
    });

  if (drift.length === 0) return { kind: 'current' };

  return { kind: 'outdated', drift, breaksSeries: drift.some((change) => change.breaksSeries) };
}

/**
 * The freshness of a group's engines as upstream's «Desactualizado» says it, or null from a
 * Virtual Profiler older than it — then the probe decides (`engineFreshness`).
 *
 * Upstream compares with the versions deployed **now** (the live workers'), not with the last
 * analysis that happened to record some, so its word replaces the probe's. Only the engines' part
 * of the answer is read here: a group flagged because its configuration has a newer version is
 * current in its engines, and its flag is `reanalysisFlag`'s to show. The queue's word on «running»
 * comes first, as in `engineFreshness`: it sees a relaunch before the list does.
 */
export function freshnessFromReanalysis(
  reanalysis: GroupReanalysis | null | undefined,
  inFlight: boolean,
): EngineFreshness | null {
  if (!reanalysis) return null;
  if (inFlight || reanalysis.status === 'running') return { kind: 'running' };
  if (reanalysis.status === 'never_run') return { kind: 'never_run' };

  const drift: EngineDrift[] = reanalysis.reasons
    .flatMap((reason) => (reason.code === 'engine_version_changed' ? [reason] : []))
    .sort((a, b) => engineOrder(a.engine, b.engine))
    .map((reason) => ({
      engine: reason.engine,
      before: reason.from || null,
      after: reason.to,
      breaksSeries: breaksSeriesOf(reason.engine, reason.from || null, reason.to),
    }));

  if (drift.length > 0) {
    return { kind: 'outdated', drift, breaksSeries: drift.some((change) => change.breaksSeries) };
  }

  return reanalysis.engineVersionsChecked
    ? { kind: 'current' }
    : { kind: 'unknown', reason: 'check_failed' };
}

/** Whether a group is worth an engine probe: it has run and nothing of it is moving. */
export function needsEngineProbe(
  group: Pick<AnalysisGroup, 'runs' | 'lastBatch' | 'runsByStatus'>,
): boolean {
  return group.runs > 0 && groupInFlight(group) === 0;
}

/** The next `width` ids of `order` that have not settled yet. */
export function probeWindow(
  order: readonly number[],
  settled: ReadonlySet<number>,
  width: number = PROBE_WIDTH,
): number[] {
  if (width <= 0) return [];

  const window: number[] = [];

  for (const id of order) {
    if (window.length >= width) break;
    if (!settled.has(id) && !window.includes(id)) window.push(id);
  }

  return window;
}

/**
 * «Gestos 3.0.2 → 3.1.0 · Veracidad — → 3.0.0»: each engine by its short label, never by its
 * identifier.
 *
 * Takes any engine change, not only a freshness drift: the execution lanes print the same line
 * from `diffEngineVersions`, whose `after` can be null (an engine that stopped running).
 */
export function driftLine(drift: readonly EngineChange[]): string {
  return drift
    .map(
      (change) =>
        `${engineShortName(change.engine)} ${change.before ?? '—'} → ${change.after ?? '—'}`,
    )
    .join(' · ');
}

/** «Reanalizar con Gestos 3.1.0 y Veracidad 3.0.0», or null unless outdated. */
export function reanalyseLabel(freshness: EngineFreshness): string | null {
  if (freshness.kind !== 'outdated' || freshness.drift.length === 0) return null;

  return `Reanalizar con ${AND_LIST.format(
    freshness.drift.map((change) => `${engineShortName(change.engine)} ${change.after}`),
  )}`;
}

/**
 * « Empezará una serie nueva: cambia Gestos 3.0 → 3.1.», or '' when the series holds.
 *
 * With its leading space, because it is appended to a confirmation's description. By major.minor,
 * the part of the version the series key carries: the patch is not what breaks it.
 */
export function seriesBreakNote(freshness: EngineFreshness): string {
  if (freshness.kind !== 'outdated' || !freshness.breaksSeries) return '';

  const breaking = freshness.drift.filter((change) => change.breaksSeries);
  const changes = breaking.map(
    (change) =>
      `${engineShortName(change.engine)} ${majorMinor(change.before) ?? '—'} → ${majorMinor(change.after) ?? '—'}`,
  );

  return ` Empezará una serie nueva: ${changes.length === 1 ? 'cambia' : 'cambian'} ${AND_LIST.format(changes)}.`;
}

export type ConfigFreshness =
  | { kind: 'never_run' }
  | { kind: 'unknown' }
  | { kind: 'current'; name: string; version: number }
  | { kind: 'newer'; name: string; from: number; to: number };

/**
 * Whether the configuration the group's latest execution ran with has a newer version now: one of
 * the two reasons a group is «Desactualizado» (upstream's `config_version_changed`), worked out here
 * from the configurations list as well, so the board can say it before — or without — upstream.
 *
 * Upstream's answer wins when it sent one: it compares the versions' content (a new version saved
 * with the same content is not newer), which a version number cannot. Without it, by version
 * number. A configuration missing from the list is an anomaly (or a list not loaded yet), not a
 * danger: it reads as unknown rather than as something to act on.
 */
export function configFreshness(
  group: Pick<AnalysisGroup, 'latestParameters'> & Partial<Pick<AnalysisGroup, 'reanalysis'>>,
  configs: readonly Pick<AnalysisConfigSummary, 'id' | 'name' | 'version'>[] | undefined,
): ConfigFreshness {
  const latest = group.latestParameters;
  if (!latest) return { kind: 'never_run' };

  const ref = latest.config;
  if (!ref || ref.id === null || ref.version === null || !configs) return { kind: 'unknown' };

  const config = configs.find((candidate) => candidate.id === ref.id);
  if (!config || config.version === null) return { kind: 'unknown' };

  const name = ref.name ?? config.name;
  const reanalysis = group.reanalysis;

  if (reanalysis && (reanalysis.status === 'up_to_date' || reanalysis.status === 'outdated')) {
    const moved = reanalysis.reasons.find((reason) => reason.code === 'config_version_changed');

    return moved && moved.from !== null && moved.to !== null
      ? { kind: 'newer', name, from: moved.from, to: moved.to }
      : { kind: 'current', name, version: ref.version };
  }

  return config.version > ref.version
    ? { kind: 'newer', name, from: ref.version, to: config.version }
    : { kind: 'current', name, version: ref.version };
}

/** Where an engine sits in the catalogue; one the catalogue does not know goes after every known one. */
function catalogueRank(name: string): number {
  const index = DEFAULT_ENGINE_CATALOGUE.findIndex((entry) => entry.name === name);

  return index === -1 ? DEFAULT_ENGINE_CATALOGUE.length : index;
}

/** «Gestos + Veracidad», in catalogue order; '—' when there are none. */
export function engineNames(engines: readonly string[]): string {
  if (engines.length === 0) return '—';

  return [...new Set(engines)]
    .sort((a, b) => catalogueRank(a) - catalogueRank(b) || a.localeCompare(b))
    .map((name) => engineLabel(DEFAULT_ENGINE_CATALOGUE, name))
    .join(' + ');
}

export interface FreshnessDigest {
  /** Groups whose engines can be checked at all: they ran, are not moving, and there is a worker. */
  checkable: number;
  /** Of those, the ones already probed. */
  checked: number;
  /** Measured with an engine version older than the one deployed. */
  outdated: number[];
  breaking: number[];
  /** Their latest execution's configuration has a newer version now. */
  configNewer: number[];
  /**
   * Every group «Desactualizado», for either reason — an engine or the configuration moved on,
   * as upstream says it or as the board works it out — and not in flight: what «Reanalizar N»
   * relaunches.
   */
  relaunch: number[];
}

export function freshnessDigest(
  groups: readonly (Pick<AnalysisGroup, 'id'> & Partial<Pick<AnalysisGroup, 'reanalysis'>>)[],
  engine: ReadonlyMap<number, EngineFreshness>,
  config: ReadonlyMap<number, ConfigFreshness>,
): FreshnessDigest {
  const digest: FreshnessDigest = {
    checkable: 0,
    checked: 0,
    outdated: [],
    breaking: [],
    configNewer: [],
    relaunch: [],
  };

  for (const { id, reanalysis } of groups) {
    const freshness = engine.get(id);

    if (
      freshness &&
      freshness.kind !== 'never_run' &&
      freshness.kind !== 'running' &&
      !(freshness.kind === 'unknown' && freshness.reason === 'no_worker')
    ) {
      digest.checkable += 1;
      if (!(freshness.kind === 'unknown' && freshness.reason === 'not_checked')) {
        digest.checked += 1;
      }
    }

    if (freshness?.kind === 'outdated') {
      digest.outdated.push(id);
      if (freshness.breaksSeries) digest.breaking.push(id);
    }

    const newer = config.get(id)?.kind === 'newer';
    if (newer) digest.configNewer.push(id);

    const flagged = freshness?.kind === 'outdated' || newer || reanalysis?.status === 'outdated';
    if (flagged && freshness?.kind !== 'running') digest.relaunch.push(id);
  }

  return digest;
}
